import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Package, Search, Loader2, MapPin, ArrowRight, Eye,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  BOOKED: "bg-blue-100 text-blue-700 border-blue-200",
  PICKED_UP: "bg-orange-100 text-orange-700 border-orange-200",
  IN_TRANSIT: "bg-purple-100 text-purple-700 border-purple-200",
  DELIVERED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  DISPUTED: "bg-red-100 text-red-700 border-red-200",
  BOOKED_LC: "bg-blue-100 text-blue-700 border-blue-200",
};

function statusClass(status) {
  const key = (status || "").toUpperCase().replace(/-/g, "_");
  return STATUS_BADGE[key] || "bg-slate-100 text-slate-600 border-slate-200";
}

function fmt(n) {
  return `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PartnerShipments() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const partnerId = user?.partner_id || user?.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchShipments = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/shipments?partner_id=${partnerId}`, { headers });
      if (res.ok) {
        const d = await res.json();
        setItems(d.items || d || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  useEffect(() => { fetchShipments(); }, [fetchShipments]);

  const filtered = items.filter((s) => {
    if (statusFilter !== "All") {
      const normalized = (s.status || "").toUpperCase().replace(/-/g, "_");
      if (normalized !== statusFilter.toUpperCase().replace(/ /g, "_")) return false;
    }
    if (dateFrom && (s.created_at || "") < dateFrom) return false;
    if (dateTo && (s.created_at || "") > dateTo + "T23:59:59") return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(s.awb || "").toLowerCase().includes(q) &&
          !(s.tracking_id || "").toLowerCase().includes(q) &&
          !(s.order_id || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <DashboardLayout portalColor="green">
      <div className="space-y-4" data-testid="partner-shipments">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">My Shipments</h1>
            <p className="text-sm text-slate-500">{filtered.length} of {items.length} shipments</p>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="bg-white border rounded-lg p-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded h-9 px-2 text-sm"
                    data-testid="filter-status">
              <option>All</option>
              <option>Booked</option>
              <option>Picked Up</option>
              <option>In Transit</option>
              <option>Delivered</option>
              <option>Disputed</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="h-9 text-sm" data-testid="filter-date-to" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Search AWB / Order ID</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     className="pl-7 h-9 text-sm" placeholder="AWB or Order ID..."
                     data-testid="filter-search" />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">AWB</th>
                  <th className="text-left px-3 py-2 font-semibold">Order ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Origin → Destination</th>
                  <th className="text-left px-3 py-2 font-semibold">Weight</th>
                  <th className="text-left px-3 py-2 font-semibold">Service</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No shipments found
                  </td></tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50"
                        data-testid={`shipment-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono text-[11px]">{s.awb || s.tracking_id || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{s.order_id || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 text-[11px]">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          <span>{s.origin?.city || s.origin?.pincode || "—"}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                          <span className="font-medium">{s.destination?.city || s.destination?.pincode || "—"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {s.chargeable_weight || s.package?.weight || 0} kg
                      </td>
                      <td className="px-3 py-2">
                        <Badge className="text-[10px] bg-slate-100 text-slate-700">
                          {s.service_level || "STANDARD"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`text-[10px] border ${statusClass(s.status)}`}>
                          {(s.status || "BOOKED").toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="outline" size="sm" onClick={() => setSelected(s)}
                                data-testid={`view-btn-${s.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* DETAILS MODAL (partner-safe view) */}
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Shipment Details</DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-3 text-sm" data-testid="shipment-details">
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="AWB" value={selected.awb || selected.tracking_id || "—"} mono />
                  <Detail label="Order ID" value={selected.order_id || "—"} mono />
                  <Detail label="Status" value={(selected.status || "BOOKED").toUpperCase()} />
                  <Detail label="Service Level" value={selected.service_level || "STANDARD"} />
                  <Detail label="Origin" value={`${selected.origin?.city || ""} ${selected.origin?.pincode || ""}`} />
                  <Detail label="Destination" value={`${selected.destination?.city || ""} ${selected.destination?.pincode || ""}`} />
                  <Detail label="Chargeable Weight" value={`${selected.chargeable_weight || selected.package?.weight || 0} kg`} />
                  <Detail label="Customer Price" value={fmt(selected.price || selected.customer_price || 0)} />
                  <Detail label="GST (18%)" value={fmt(selected.gst_amount || 0)} />
                  <Detail label="Your Commission" value={fmt(selected.partner_commission || 0)} />
                </div>
                <div className="bg-slate-50 border rounded px-3 py-2 text-xs text-slate-500">
                  Margin, vendor cost, and carrier internal cost are not visible to partners.
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
